import { AloftPrice } from '@aloft/shared-objects';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompAdjustments } from './adjustmentSelectors.ts';
import { selectTransactionEntities } from './propertyDataSelectors/transactionSelectors.ts';
import { selectSubjectValue } from './selectSubjectValue.ts';

export const selectReconciliationData = createSelector(
  [selectSubjectValue, selectCompAdjustments, selectTransactionEntities],
  (subjectValue, compAdjustments, transactionEntities) => {
    const compMarketAdjustedSalePrices = Object.values(compAdjustments)
      .map((adjustment) => adjustment.adjustedSalePrice?.adjustment)
      .filter((price) => price !== undefined)
      .sort((a, b) => a.toDollarLike() - b.toDollarLike());
    const maxCompMarketAdjustedSalePrice =
      compMarketAdjustedSalePrices[compMarketAdjustedSalePrices.length - 1];
    const minCompMarketAdjustedSalePrice = compMarketAdjustedSalePrices[0];
    let averageCompMarketAdjustedSalePrice: AloftPrice | undefined =
      compMarketAdjustedSalePrices.reduce(
        (acc, price) => acc.add(price),
        AloftPrice.fromDollarLike(0),
      );

    if (compMarketAdjustedSalePrices.length === 0) {
      averageCompMarketAdjustedSalePrice = undefined;
    } else {
      averageCompMarketAdjustedSalePrice = averageCompMarketAdjustedSalePrice.div(
        compMarketAdjustedSalePrices.length,
      );
    }
    const medianCompMarketAdjustedSalePrice =
      compMarketAdjustedSalePrices[Math.floor(compMarketAdjustedSalePrices.length / 2)];
    const compDaysOnMarket = Object.keys(transactionEntities)
      .filter((key) => key !== 'subject')
      .map((key) => transactionEntities[key]?.daysOnMarket)
      .filter((value) => value !== undefined)
      .filter((value) => value > 0);
    const averageCompDaysOnMarket =
      compDaysOnMarket.length > 0
        ? Math.round(
            compDaysOnMarket.reduce((acc, days) => acc + days, 0) / compDaysOnMarket.length,
          )
        : undefined;
    const medianCompDaysOnMarket =
      compDaysOnMarket.length > 0
        ? compDaysOnMarket[Math.floor(compDaysOnMarket.length / 2)]
        : undefined;

    return {
      costApproachValue: subjectValue,
      maxCompMarketAdjustedSalePrice,
      minCompMarketAdjustedSalePrice,
      averageCompMarketAdjustedSalePrice,
      medianCompMarketAdjustedSalePrice,
      averageCompDaysOnMarket,
      medianCompDaysOnMarket,
    };
  },
);
